import { TextFieldProps } from '@mui/material';
import {
  Controller,
  FieldError,
  FieldPath,
  FieldValues,
  Merge,
  UseControllerProps,
  useController,
} from 'react-hook-form';
import NumberInput from '../atoms/NumberInput';
import { useMarketplace } from '../../hooks/useMarketplace';
import { getCurrencySymbol } from '../atoms/BettingInfoDisplay';

type FormNumberInputProps<T extends FieldValues, TTransformedValues = T> = UseControllerProps<
  T,
  FieldPath<T>,
  TTransformedValues
> &
  Omit<TextFieldProps, 'error'> & {
    label?: string;
    error?: FieldError | Merge<FieldError, (FieldError | undefined)[]>;
    showHelperText?: boolean;
    allowNegative?: boolean;
    allowDecimals?: boolean;
    decimalPlaces?: number;
    valueAsString?: boolean;
    displayCurrency?: boolean;
  };

const FormNumberInput = <T extends FieldValues, TTransformedValues = T>({
  label,
  name,
  control,
  error,
  showHelperText = true,
  allowNegative = false,
  allowDecimals = false,
  decimalPlaces = 2,
  valueAsString = false,
  displayCurrency = false,
  slotProps,
  ...rest
}: FormNumberInputProps<T, TTransformedValues>) => {
  const {
    fieldState: { error: controlError },
  } = useController({ name, control });

  /* TODO: Refactor 
     - We can get the FieldError directly from the useController hook, there is no need to pass it as a prop. 
       Keep it for now, as to not break any existing code. Should retest and remove the prop in the future.
  */
  const fieldError = error ?? controlError;
  const { config } = useMarketplace();
  let currencySymbol = '';

  if (displayCurrency) {
    const marketplaceCurrency = config?.marketplaceConfig?.currency || '';
    currencySymbol = getCurrencySymbol(marketplaceCurrency) || '';
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <NumberInput
          error={!!fieldError}
          onChange={onChange}
          label={label || ''}
          value={value ?? ''}
          showHelperText={showHelperText}
          allowDecimals={allowDecimals}
          allowNegative={allowNegative}
          valueAsString={valueAsString}
          helperText={showHelperText ? fieldError?.message : undefined}
          {...rest}
          slotProps={{
            ...slotProps,
            input: {
              ...slotProps?.input,
              ...(displayCurrency ? { endAdornment: currencySymbol } : {}),
            },
          }}
        />
      )}
    />
  );
};

export default FormNumberInput;
